/**
 * Device Handoff Utility
 *
 * PURPOSE: Move a visitor blocked by a campaign's device lock onto a compatible device
 * without losing their identity, so the incompatible-device view and the eventual
 * continue can be joined in analytics.
 *
 * Two transports, because they have very different lifetimes:
 *
 * - QR / copy link — carries `persid` + `persts` and rides the existing ID bridge in
 *   idBridge.ts. That bridge deliberately expires after 3 minutes to stop shared links
 *   from merging identities, so a QR left on screen must be re-rendered with a fresh
 *   timestamp (see HANDOFF_QR_REFRESH_MS).
 *
 * - Email — opened minutes or hours later, so `persts` would always be stale. The
 *   backend instead mints an opaque handoff token (`pch`) that maps to the persistent
 *   ID for 7 days; see resolveHandoffToken().
 *
 * Both transports also carry `pchandoff` (how they crossed) and `pchorig` (device class
 * they left), so Device Handoff Completed can attribute origin vs arrival.
 */

import type { DeviceHandoffMethod } from '../../../telemetry/client';
import { BRIDGE_EXPIRY_MS } from './idBridge';

/** Records the transport used, so the arrival event can attribute the handoff. */
export const HANDOFF_METHOD_PARAM = 'pchandoff';

/** Opaque backend-minted token used by the email transport. */
export const HANDOFF_TOKEN_PARAM = 'pch';

/** Device class the visitor left — carried so arrival can join the funnel. */
export const HANDOFF_ORIGIN_PARAM = 'pchorig';

export type HandoffOriginDevice = 'ios' | 'android' | 'desktop';

/**
 * How often the desktop QR is regenerated. Comfortably inside the bridge expiry so a
 * code sitting on a screen is always scannable, and re-rendering is cheap.
 */
export const HANDOFF_QR_REFRESH_MS = Math.floor(BRIDGE_EXPIRY_MS / 2);

const BRIDGE_PARAMS = [
  'persid',
  'persts',
  HANDOFF_METHOD_PARAM,
  HANDOFF_TOKEN_PARAM,
  HANDOFF_ORIGIN_PARAM,
];

const isHandoffMethod = (value: string | null): value is DeviceHandoffMethod =>
  value === 'qr' || value === 'email' || value === 'copy';

const isOriginDevice = (value: string | null): value is HandoffOriginDevice =>
  value === 'ios' || value === 'android' || value === 'desktop';

/**
 * Build the URL to hand to the other device.
 *
 * Preserves the campaign path and any existing query (UTMs, dynamic params) and stamps
 * a fresh bridge timestamp, so the returned URL is only valid for BRIDGE_EXPIRY_MS.
 */
export function buildHandoffUrl(
  persistentId: string,
  method: DeviceHandoffMethod,
  originDeviceType?: HandoffOriginDevice,
): string {
  const url = new URL(window.location.href);

  // Drop any inbound bridge params so we never stack a stale pair onto a fresh one.
  BRIDGE_PARAMS.forEach((param) => url.searchParams.delete(param));

  url.searchParams.set('persid', persistentId);
  url.searchParams.set('persts', Date.now().toString());
  url.searchParams.set(HANDOFF_METHOD_PARAM, method);
  if (originDeviceType) {
    url.searchParams.set(HANDOFF_ORIGIN_PARAM, originDeviceType);
  }

  return url.toString();
}

/**
 * Read how the visitor arrived, if they arrived via a handoff.
 *
 * MUST be called before removePersistentIdFromUrl() in the ID bridge setup, which
 * strips the query params this reads.
 */
export function readHandoffMethodFromUrl(): DeviceHandoffMethod | null {
  const method = new URLSearchParams(window.location.search).get(HANDOFF_METHOD_PARAM);
  return isHandoffMethod(method) ? method : null;
}

/** Read the emailed handoff token, if present. */
export function readHandoffTokenFromUrl(): string | null {
  const token = new URLSearchParams(window.location.search).get(HANDOFF_TOKEN_PARAM);
  return token && token.length > 0 ? token : null;
}

/**
 * Read the originating device class stamped by the blocked device.
 *
 * `pchorig` alone is untrusted (anyone can tack it onto a URL). The coordinator only
 * keeps this value when a real bridge or handoff token also carried identity over — so a
 * fabricated origin can colour a completion that already happened, never invent one.
 */
export function readHandoffOriginFromUrl(): HandoffOriginDevice | null {
  const origin = new URLSearchParams(window.location.search).get(HANDOFF_ORIGIN_PARAM);
  return isOriginDevice(origin) ? origin : null;
}

/** Strip handoff params so the URL is clean and not re-shareable as an identity. */
export function removeHandoffParamsFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;

  [HANDOFF_METHOD_PARAM, HANDOFF_TOKEN_PARAM, HANDOFF_ORIGIN_PARAM].forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      changed = true;
    }
  });

  if (changed) {
    window.history.replaceState({}, '', url.toString());
  }
}
